import React from 'react';

/**
 * ProjectImage - Creative placeholder for project images
 * Generates unique visual patterns based on project type
 *
 * @param {object} props
 * @param {string} props.type - Project type (fintech, devops, fullstack, backend)
 * @param {string} props.title - Project title
 * @param {string} [props.className=''] - Additional CSS classes
 */
const ProjectImage = ({ type, title, className = '' }) => {
  // Color schemes based on project type
  const typeStyles = {
    fintech: {
      gradient: 'from-emerald-500 via-teal-500 to-cyan-500',
      pattern: 'fintech',
      icon: '💰'
    },
    devops: {
      gradient: 'from-orange-500 via-red-500 to-pink-500',
      pattern: 'devops',
      icon: '⚙️'
    },
    fullstack: {
      gradient: 'from-violet-500 via-purple-500 to-indigo-500',
      pattern: 'fullstack',
      icon: '⚡'
    },
    backend: {
      gradient: 'from-blue-500 via-cyan-500 to-teal-500',
      pattern: 'backend',
      icon: '🔧'
    }
  };

  const style = typeStyles[type] || typeStyles.fullstack;

  // Generate unique pattern based on title hash
  const hashCode = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash);
  };

  const titleHash = hashCode(title);
  const patternSeed = titleHash % 4;

  return (
    <div
      className={`relative w-full h-48 overflow-hidden rounded-lg ${className}`}
      style={{
        background: `linear-gradient(135deg, var(--color-bg-card) 0%, rgba(0, 212, 170, 0.1) 100%)`
      }}
    >
      {/* Animated Gradient Background */}
      <div
        className={`absolute inset-0 bg-gradient-to-br ${style.gradient} opacity-20 animate-gradient`}
        style={{
          backgroundSize: '200% 200%'
        }}
      />

      {/* Pattern Overlay */}
      <svg className="absolute inset-0 w-full h-full opacity-10">
        <defs>
          {/* Grid Pattern */}
          {patternSeed === 0 && (
            <pattern id={`grid-${titleHash}`} width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1"/>
            </pattern>
          )}

          {/* Dots Pattern */}
          {patternSeed === 1 && (
            <pattern id={`dots-${titleHash}`} width="20" height="20" patternUnits="userSpaceOnUse">
              <circle cx="10" cy="10" r="2" fill="currentColor"/>
            </pattern>
          )}

          {/* Diagonal Lines */}
          {patternSeed === 2 && (
            <pattern id={`diag-${titleHash}`} width="10" height="10" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="10" stroke="currentColor" strokeWidth="1"/>
            </pattern>
          )}

          {/* Hexagons */}
          {patternSeed === 3 && (
            <pattern id={`hex-${titleHash}`} width="30" height="26" patternUnits="userSpaceOnUse">
              <path d="M15,0 L30,7.5 L30,19.5 L15,26 L0,19.5 L0,7.5 Z" fill="none" stroke="currentColor" strokeWidth="1"/>
            </pattern>
          )}
        </defs>

        <rect
          width="100%"
          height="100%"
          fill={`url(#${patternSeed === 0 ? 'grid' : patternSeed === 1 ? 'dots' : patternSeed === 2 ? 'diag' : 'hex'}-${titleHash})`}
          style={{ color: 'rgba(255, 255, 255, 0.5)' }}
        />
      </svg>

      {/* Floating Icon */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-6xl opacity-40 animate-float">
          {style.icon}
        </div>
      </div>

      {/* Code-like decorative elements */}
      <div className="absolute top-4 left-4 font-mono text-xs opacity-20">
        {'<'}{type}{'>'}
      </div>
      <div className="absolute bottom-4 right-4 font-mono text-xs opacity-20">
        {'</'}{type}{'>'}
      </div>

      {/* Gradient Overlay at bottom */}
      <div
        className="absolute bottom-0 left-0 right-0 h-20"
        style={{
          background: 'linear-gradient(to top, var(--color-bg-card), transparent)'
        }}
      />
    </div>
  );
};

export default ProjectImage;
